import { createAsyncThunk, AnyAction } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import i18n from "config/i18n"
import { API } from "redux/api"
import {
  ACTION_STATUS_SUCCESS,
  POLLING_INTERVAL,
  POLLING_TIMEOUT,
} from "config"
import {
  start as startPolling,
  reset as resetPolling,
} from "redux/features/polling/pollingSlice"
import { ProductPaymentValues, ProductPaymentThunkResponseType } from "types"

export const createProductPaymentRequestSession = createAsyncThunk(
  "product-payment-request-session/create",
  async (values: ProductPaymentValues) => {
    const response = await API.post("/payment_requests", values)
    return response.data
  },
)

export const fetchProductPaymentRequestSession = createAsyncThunk(
  "product-payment-request-session/fetch",
  async (values: { session_id: string }) => {
    const response = await API.get(
      `/payment_requests/session/${values.session_id}`,
    )

    return response.data
  },
)

export const makePayment = (values: ProductPaymentValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise<ProductPaymentThunkResponseType>((resolve, reject) => {
      return dispatch(createProductPaymentRequestSession(values))
        .then(
          ({ payload }) => {
            if (!payload || !payload.payment_request) {
              return reject(i18n.t("notice.payment.session.unavailable"))
            }

            let payment_result: ProductPaymentThunkResponseType

            const timerId = setInterval(async () => {
              const { session_id } = payload.payment_request
              const result = await dispatch(
                fetchProductPaymentRequestSession({ session_id }),
              )

              if (result?.payload?.session) {
                const { status, action_payload } = result.payload.session

                // NOTE: clear interval when status = success
                if (status === ACTION_STATUS_SUCCESS) {
                  clearInterval(timerId)
                  dispatch(resetPolling())

                  const { request_uri, request_params, request_method } =
                    action_payload

                  payment_result = {
                    request_uri,
                    request_method,
                    request_params,
                  }

                  resolve(payment_result)
                }
              }
            }, POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout(() => {
              clearInterval(timerId)
              dispatch(resetPolling())
              if (!payment_result) {
                reject(i18n.t("notice.payment.create.failure"))
              }
            }, POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.payment.session.failure"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}
